/**
 * P47 (Feb 28, 2026) — Global ODM Environment Indicator.
 *
 * A tiny header badge that surfaces which ODM environment is currently
 * receiving real EDI submissions (CERT sandbox or PRODUCTION live).
 * Visible to AZAI super-admins only — agency staff don't need this and
 * we don't want to scare them with the word "PRODUCTION".
 *
 * The indicator clicks through to /admin/odm-credentials where the
 * admin can flip the active environment.
 *
 * Why this exists: before P47, the active env was only visible on the
 * ODM Credentials page. After P46 (where a stale env caused TA1 reject
 * 020 to block every real submission for hours), it became clear that
 * the active env needs to be impossible to forget about, no matter
 * which page you're on.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Warning, ArrowsClockwise } from '@phosphor-icons/react';
import { useAgency } from '../context/AgencyContext';
import { odmCredentialsApi } from '../services/api';

const OdmEnvBadge = () => {
  const { isAzaiStaff } = useAgency();
  const [activeEnv, setActiveEnv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAzaiStaff) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    odmCredentialsApi.getActive()
      .then((res) => {
        if (!cancelled) setActiveEnv(res.data.active_environment);
      })
      .catch(() => { /* silent — banner just won't show */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAzaiStaff]);

  // Re-poll every 30s so a flip done in another tab shows up.
  useEffect(() => {
    if (!isAzaiStaff) return;
    const t = setInterval(() => {
      odmCredentialsApi.getActive()
        .then((res) => setActiveEnv(res.data.active_environment))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [isAzaiStaff]);

  if (!isAzaiStaff || loading || !activeEnv) return null;

  const isProd = activeEnv === 'prod';
  const styles = isProd
    ? 'bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100'
    : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100';
  const Icon = isProd ? ShieldCheck : Warning;
  const label = isProd ? 'ODM: PROD' : 'ODM: CERT';
  const tooltip = isProd
    ? 'All EDI submissions are going to LIVE Ohio Medicaid. Click to manage.'
    : 'EDI submissions are going to the CERT sandbox. Click to manage.';

  return (
    <Link
      to="/admin/odm-credentials"
      title={tooltip}
      className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold transition-colors ${styles}`}
      data-testid="odm-env-badge"
    >
      <Icon size={14} weight={isProd ? 'fill' : 'bold'} />
      {label}
    </Link>
  );
};

export default OdmEnvBadge;
